import { useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import avaturnCustomAnimationConfig from '../../config/avaturnCustomAnimationConfig';
import { applyAvaturnBasePose } from '../../helpers/animation';
import {
  CUSTOM_ANIMATION_BONE_NAMES,
  CUSTOM_ANIMATION_MORPH_NAMES,
  advanceCustomAnimationState,
  computeBreathOffsets,
  computeCustomAnimationMorphValues,
  createCustomAnimationState,
  createSeededRandom
} from '../../helpers/avaturnCustomAnimation';

/**
 * Custom animation for multi-character scenes: breathing, randomised blinks and
 * small eye saccades, standing in for the looping avaturn_animation clip.
 *
 * Writes only CUSTOM_ANIMATION_MORPH_NAMES, the morphs that clip already owns and that both
 * the emotion overlay and viseme15 lipsync leave alone, plus CUSTOM_ANIMATION_BONE_NAMES,
 * which nothing else in the app touches. So it needs no layering against either,
 * and its frame priority does not matter; the default (0) is used because R3F
 * stops rendering automatically for any priority above 0.
 *
 * `renderRoot` must be AvatarLoader's group ref, not the useGLTF scene: mounting
 * the skeleton with `<primitive object={nodes.Hips} />` reparents Hips out of
 * that scene, so a scene-rooted lookup would find no bones at all. The group
 * holds both the reparented bones and the rendered meshes, whose influence
 * arrays alias the originals.
 */
export const useAvaturnCustomAnimation = ({
  renderRoot,
  basePose,
  seedKey = '',
  enabled = false
}) => {
  const rigRef = useRef(null);
  const stateRef = useRef(null);
  const morphValuesRef = useRef({});
  const breathOffsetsRef = useRef({});
  const offsetRef = useRef(new THREE.Quaternion());
  const eulerRef = useRef(new THREE.Euler());

  const random = useMemo(() => createSeededRandom(seedKey), [seedKey]);

  // Layout effect, not a passive one: React 18 can flush passive effects after
  // paint, and the canvas renders every frame, so the avatar would show its
  // T-pose for a frame before the base pose landed.
  useLayoutEffect(() => {
    const root = renderRoot?.current;
    if (!enabled || !root || !basePose) return undefined;

    applyAvaturnBasePose(root, basePose);

    const bones = [];
    const meshes = [];

    root.traverse((object) => {
      if (object.isBone) {
        if (CUSTOM_ANIMATION_BONE_NAMES.includes(object.name)) {
          bones.push({
            name: object.name,
            bone: object,
            base: object.quaternion.clone()
          });
        }
        return;
      }

      if (
        !object.isMesh ||
        !object.morphTargetDictionary ||
        !object.morphTargetInfluences
      ) {
        return;
      }

      // The same morph sits at a different index on each mesh, so resolve every
      // index once here and never look one up per frame.
      const targets = CUSTOM_ANIMATION_MORPH_NAMES.map((morphName) => ({
        morphName,
        index: object.morphTargetDictionary[morphName]
      })).filter(({ index }) => index !== undefined);

      if (targets.length) {
        meshes.push({ influences: object.morphTargetInfluences, targets });
      }
    });

    rigRef.current = { bones, meshes };
    stateRef.current = createCustomAnimationState(random);

    return () => {
      // useGLTF is cached and uncloned here, so leave nothing behind for the
      // next mount of the same model to inherit. Restoring the base pose rather
      // than the GLB rest pose keeps the avatar out of its T-pose.
      meshes.forEach(({ influences, targets }) => {
        targets.forEach(({ index }) => {
          influences[index] = 0;
        });
      });
      applyAvaturnBasePose(root, basePose);

      rigRef.current = null;
      stateRef.current = null;
    };
    // `basePose` is derived from the GLB's own clips, so it is what changes
    // when useGLTF swaps models — the rig is re-read off that, not off `nodes`.
  }, [renderRoot, basePose, random, enabled]);

  useFrame((_, delta) => {
    const rig = rigRef.current;
    const state = stateRef.current;
    if (!enabled || !rig || !state) return;

    advanceCustomAnimationState(state, delta, avaturnCustomAnimationConfig);

    const morphValues = computeCustomAnimationMorphValues(
      state,
      avaturnCustomAnimationConfig,
      morphValuesRef.current
    );
    rig.meshes.forEach(({ influences, targets }) => {
      targets.forEach(({ morphName, index }) => {
        influences[index] = morphValues[morphName];
      });
    });

    const breathOffsets = computeBreathOffsets(
      state,
      avaturnCustomAnimationConfig,
      breathOffsetsRef.current
    );
    rig.bones.forEach(({ name, bone, base }) => {
      eulerRef.current.set(breathOffsets[name] ?? 0, 0, 0);
      offsetRef.current.setFromEuler(eulerRef.current);
      // Always base x offset, so breathing never accumulates frame to frame.
      bone.quaternion.copy(base).multiply(offsetRef.current);
    });
  });
};
